import React from 'react'
import { format, parseISO } from 'date-fns'
import { formatCurrency } from '../../../utils/formatters'
import FulfillmentGroupCard from './FulfillmentGroupCard'
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline' // For Message button
import { UserCircleIcon } from '@heroicons/react/24/solid' // Placeholder for profile pic
import Button from '../../ui/Button' // For the Message button
import PaymentStatusBadge from '../../ui/PaymentStatusBadge'
import CustomerProfilePicture from '../../ui/CustomerProfilePicture'
import OrderActionButtons from './OrderActionButtons'
import { useHistory } from 'react-router-dom' // For navigation

// Helper Functions (could be moved to utils if reused)
const userLang = navigator.language || navigator.userLanguage

// Helper mappings for colors - define locally if not using Badge component
const typeColorClasses = {
  gray: 'bg-gray-100 text-gray-600',
  red: 'bg-red-100 text-red-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  green: 'bg-green-100 text-green-800',
  blue: 'bg-blue-100 text-blue-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  purple: 'bg-purple-100 text-purple-800',
  pink: 'bg-pink-100 text-pink-800',
  teal: 'bg-teal-100 text-teal-800',
  orange: 'bg-orange-100 text-orange-800'
}

// Helper functions for Order Status (using order.status directly)
const getOrderStatusColor = status => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800'
    case 'payment_failed':
      return 'bg-red-100 text-red-800'
    case 'paid':
      return 'bg-green-100 text-green-800'
    case 'refund_pending':
      return 'bg-orange-100 text-orange-800'
    case 'partially_refunded':
      return 'bg-orange-100 text-orange-800'
    case 'fully_refunded':
      return 'bg-orange-100 text-orange-800'
    case 'deposit_pending_refund':
      return 'bg-yellow-100 text-yellow-800'
    case 'deposit_refunded':
      return 'bg-green-100 text-green-800'
    case 'completed':
      return 'bg-green-100 text-green-800'
    case 'canceled':
      return 'bg-red-100 text-red-800'
    // Legacy statuses for backward compatibility
    case 'processing':
      return 'bg-blue-100 text-blue-800'
    case 'shipped':
      return 'bg-purple-100 text-purple-800'
    case 'delivered':
      return 'bg-green-100 text-green-800'
    case 'refunded':
      return 'bg-orange-100 text-orange-800'
    case 'returned':
      return 'bg-gray-100 text-gray-800'
    case 'return_pending':
    case 'return_requested':
    case 'return_approved':
    case 'return_shipped':
    case 'return_delivered':
      return 'bg-orange-100 text-orange-800'
    case 'in_use':
      return 'bg-teal-100 text-teal-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getOrderStatusText = status => {
  const statusLower = status?.toLowerCase() || 'unknown'
  let defaultText = statusLower
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())

  if (userLang === 'zh-CN') {
    switch (statusLower) {
      case 'pending':
        return '待付款'
      case 'payment_failed':
        return '付款失败'
      case 'paid':
        return '已支付'
      case 'refund_pending':
        return '退款处理中'
      case 'partially_refunded':
        return '部分退款'
      case 'fully_refunded':
        return '已退款'
      case 'deposit_pending_refund':
        return '押金退款中'
      case 'deposit_refunded':
        return '押金已退'
      case 'completed':
        return '已完成'
      case 'canceled':
        return '已取消'
      // Legacy statuses for backward compatibility
      case 'processing':
        return '处理中'
      case 'shipped':
        return '已发货'
      case 'delivered':
        return '已送达'
      case 'refunded':
        return '已退款'
      case 'returned':
        return '已退货'
      case 'return_pending':
        return '退货处理中'
      case 'return_requested':
        return '申请退货中'
      case 'return_approved':
        return '退货已批准'
      case 'return_shipped':
        return '退货已发出'
      case 'return_delivered':
        return '退货已收到'
      case 'in_use':
        return '租用中'
      default:
        return '未知状态'
    }
  }
  return defaultText
}

/**
 * Extract the primary currency from order or fulfillment groups
 * @param {Object} order - The order object
 * @returns {string} - The currency code (defaults to USD)
 */
const getOrderCurrency = order => {
  // First check if order has a currency property
  if (order?.currency) {
    return order.currency
  }

  // Check first fulfillment group and its first item
  if (order?.fulfillmentGroups && order.fulfillmentGroups.length > 0) {
    const firstGroup = order.fulfillmentGroups[0]

    // Check if the fulfillment group has a currency
    if (firstGroup.currency) {
      return firstGroup.currency
    }

    // Check if there are order items with a currency
    if (firstGroup.orderItems && firstGroup.orderItems.length > 0) {
      const firstItem = firstGroup.orderItems[0]
      if (firstItem.currency) {
        return firstItem.currency
      }
    }
  }

  // Default to USD if no currency is found
  return 'USD'
}

const OrderCard = ({ order, orderId, viewContext }) => {
  const history = useHistory() // Add history hook

  if (!order) {
    return null
  }

  const {
    orderNumber,
    createdAt,
    created_at,
    total,
    fulfillmentGroups = [],
    status: orderStatus,
    orderType,
    source, // Added in Transactions.js for context
    users_permissions_user: customer // Customer details for merchant view
  } = order

  // Get the currency for this order
  const orderCurrency = getOrderCurrency(order)

  const isMerchantView = source === 'merchant' // Simplified based on passed 'source'
  const isUserView = source === 'my'

  const displayOrderStatusText = getOrderStatusText(orderStatus)
  const displayOrderStatusColor = getOrderStatusColor(orderStatus)

  const orderDate =
    createdAt || created_at
      ? format(parseISO(createdAt || created_at), 'PPP')
      : 'Date Unknown'

  let orderTypeDisplayLabel = 'Unknown Type'
  let orderTypeDisplayColor = 'gray'

  if (source === 'my') {
    if (orderType === 'purchase' || orderType === 'swap') {
      orderTypeDisplayLabel = userLang === 'zh-CN' ? '购买' : 'Buying'
      orderTypeDisplayColor = 'blue'
    } else if (orderType === 'rental') {
      orderTypeDisplayLabel = userLang === 'zh-CN' ? '租赁' : 'Renting'
      orderTypeDisplayColor = 'green'
    } else if (orderType === 'mixed') {
      orderTypeDisplayLabel = userLang === 'zh-CN' ? '混合订单' : 'Mixed Order'
      orderTypeDisplayColor = 'yellow' // Or another color like 'orange'
    }
  } else if (source === 'merchant') {
    if (orderType === 'purchase' || orderType === 'swap') {
      orderTypeDisplayLabel = userLang === 'zh-CN' ? '销售' : 'Selling'
      orderTypeDisplayColor = 'purple'
    } else if (orderType === 'rental') {
      orderTypeDisplayLabel = userLang === 'zh-CN' ? '出租' : 'Lending'
      orderTypeDisplayColor = 'indigo'
    } else if (orderType === 'mixed') {
      orderTypeDisplayLabel = userLang === 'zh-CN' ? '混合订单' : 'Mixed Order'
      orderTypeDisplayColor = 'yellow' // Or another color like 'orange'
    }
  }

  return (
    <div className='bg-white shadow overflow-hidden sm:rounded-lg mb-4 border border-gray-200'>
      {/* Order Header */}
      <div className='bg-gray-50 px-4 py-3 sm:px-6 border-b border-gray-200'>
        <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-x-4 gap-y-3'>
          {isMerchantView ? (
            // Merchant View Layout
            <div className='flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 w-full'>
              {/* Left Group: Customer Profile Picture + Username (clickable) */}
              <div
                className='flex items-center space-x-3 cursor-pointer hover:bg-gray-100 rounded-lg p-2 -m-2 transition-colors'
                onClick={() => {
                  // Navigate to message page
                  if (
                    fulfillmentGroups &&
                    fulfillmentGroups.length > 0 &&
                    fulfillmentGroups[0].id
                  ) {
                    history.push(
                      `/message/fulfillment-group/${fulfillmentGroups[0].id}`
                    )
                  } else {
                    history.push('/message', {
                      targetUserId: customer?.id,
                      targetUserName: customer?.username || customer?.firstname,
                      contextType: 'order',
                      contextId: order.id
                    })
                  }
                }}
              >
                <CustomerProfilePicture
                  customer={customer}
                  fulfillmentGroups={fulfillmentGroups}
                  orderId={order.id}
                  size='md'
                  className='shrink-0'
                />
                <div className='min-w-0'>
                  <p className='text-sm font-medium text-gray-900 truncate'>
                    {customer?.username ||
                      customer?.firstname ||
                      'Unknown Customer'}
                  </p>
                  <PaymentStatusBadge order={order} className='shrink-0' />
                </div>
              </div>

              {/* Right Group: Order Info + Date */}
              <div className='flex items-center space-x-4'>
                <div className='text-right'>
                  <p className='text-sm text-gray-700'>{orderDate}</p>
                  <p className='text-xs text-gray-500'>#{orderNumber}</p>
                </div>
              </div>
            </div>
          ) : (
            // Customer View Layout
            <div className='flex flex-row justify-between items-center w-full gap-x-4'>
              {/* Left: Order Total */}
              <div className='flex flex-col items-start'>
                <p className='text-sm font-semibold text-gray-900'>
                  {formatCurrency(total, orderCurrency)}
                </p>
                <PaymentStatusBadge order={order} className='shrink-0' />
              </div>

              {/* Right: Date and Type */}
              <div className='flex flex-col items-end'>
                <p className='text-sm text-gray-700'>{orderDate}</p>
                <p className='text-xs text-gray-500'>#{orderNumber}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fulfillment Groups */}
      {fulfillmentGroups &&
        fulfillmentGroups.map((group, index) => (
          <FulfillmentGroupCard
            key={group.id || `fg-${index}`}
            fulfillmentGroup={group}
            orderId={orderId} // Pass down parent orderId
            orderNumber={orderNumber} // Pass down parent orderNumber
            viewContext={viewContext} // Pass down viewContext
            orderSource={source} // Pass down order source ('my' or 'merchant')
            parentOrder={order} // Pass the whole parent order for more context if needed by FGCard or ItemCard
            orderCurrency={orderCurrency} // Pass down order currency
          />
        ))}

      {/* Order Action Buttons - Only for Merchant View, at the bottom */}
      {isMerchantView && (
        <div className='bg-gray-50 px-4 py-3 sm:px-6 border-t border-gray-200'>
          <OrderActionButtons
            order={order}
            isMerchantContext={true}
            isUserContext={false}
          />
        </div>
      )}
    </div>
  )
}

export default OrderCard
